/**
 * LiveSession — submitProof + txHash no card final
 * +10 RUA MINT NA POLYGON MAINNET
 * with Grok services
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Camera, Clock, Wallet } from 'lucide-react';
import { connectWallet } from '../utils/web3.js';
import { submitProofOfLife } from '../utils/submitProof.js';
import { sha256 } from '../utils/hashProof.js';
import { extractIPFSCID, isIPFSURL } from '../utils/extractIPFSCID.js';
import AdPlaceholder from './AdPlaceholder';

const API_URL = 'https://plexo-natural-api.onrender.com';
const LIVE_DURATION = 60;
const CLIP_START_AT = 30;
const CLIP_DURATION = 5;

// DETECÇÃO MOBILE
const detectMobileWallet = () => {
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const hasInjectedWeb3 = !!(window.ethereum || (window.web3 && window.web3.currentProvider));
  return {
    isMobileBrowser: isMobile,
    hasInjectedWeb3,
    isMetaMaskMobile: !!(window.ethereum && window.ethereum.isMetaMask),
    provider: window.ethereum || (window.web3 && window.web3.currentProvider)
  };
};

const LiveSession = ({ onComplete }) => {
  console.log("LiveSession DEFINITIVO carregado");

  // === ESTADO ===
  const [state, setState] = useState({
    isLive: false,
    showVideo: false,
    timeLeft: LIVE_DURATION,
    isCounting: false,
    countdown: 3,
    isRecording: false,
    recordingTime: CLIP_DURATION,
    capturedClip: null,
    error: null,
    isCheckingLimit: false,
    cameraReady: false,
    walletConnected: false,
    userAddress: null,
    ruaBalance: null,
    isPolygon: false,
    isGeneratingProof: false,
    showSuccessCard: false,
    finalResult: null
  });

  // === REFS ===
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const liveTimerRef = useRef(null);
  const loadingAnimationRef = useRef(null);
  const proofReadyAudioRef = useRef(null);

  // === UTILS ===
  const blobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const normalizeBase64 = (base64) =>
    base64?.replace(/^data:[\w\/]+;[\w=,-]*;base64,/, 'data:video/webm;codecs=vp9,opus;base64,');

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  const setError = (msg) => {
    console.error("ERRO:", msg);
    setState(prev => ({ ...prev, error: msg }));
  };

  const clearError = () => setState(prev => ({ ...prev, error: null }));

  // === WRAPPER SEGURO PARA onComplete ===
  const safeOnComplete = useCallback((data) => {
    if (typeof onComplete === 'function') {
      try {
        console.log('📞 Chamando onComplete com:', data);
        onComplete(data);
      } catch (err) {
        console.error('❌ Erro ao chamar onComplete:', err);
        setState(prev => ({
          ...prev,
          error: `Erro ao processar live: ${err.message}`,
          isGeneratingProof: false
        }));
      }
    } else {
      console.warn('onComplete não fornecido — executando fallback local');
      setState(prev => ({
        ...prev,
        error: null,
        isGeneratingProof: false
      }));
    }
  }, [onComplete]);

  // === CONECTAR CARTEIRA ===
  const handleConnectWallet = async () => {
    console.log("CONECTANDO CARTEIRA...");
    const walletInfo = detectMobileWallet();

    if (!walletInfo.provider) {
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobile) {
        const deepLink = `https://metamask.app.link/dapp/${window.location.hostname}${window.location.pathname}`;
        window.location.href = deepLink;
        return;
      }
      return setError('MetaMask não detectada. Instale ou use em mobile.');
    }

    try {
      const result = await connectWallet();
      setState(prev => ({
        ...prev,
        walletConnected: true,
        userAddress: result.address,
        ruaBalance: result.ruaBalance,
        isPolygon: result.chainId === 137,
      }));
      console.log("CARTEIRA CONECTADA:", result);
    } catch (err) {
      console.error("Erro ao conectar:", err);
      setError(err.message || 'Falha ao conectar carteira');
    }
  };

  // === VERIFICAR LIMITE ===
  const checkLiveLimit = async () => {
    const token = localStorage.getItem('token');
    if (!token || token === 'undefined' || token === 'null' || token.length < 10) {
      console.log('Token não disponível, continuando...');
      return true;
    }

    try {
      setState(prev => ({ ...prev, isCheckingLimit: true }));
      const res = await fetch(`${API_URL}/live-limit`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });

      if (res.status === 403) return true;
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      if (data.willDeleteOldest && data.oldestLive) {
        const confirmed = window.confirm(
          `Sua próxima live substituirá a mais antiga (${new Date(data.oldestLive.timestamp).toLocaleString('pt-BR')}). Continuar?`
        );
        return confirmed;
      }
      return true;
    } catch (error) {
      console.error('Erro ao verificar limite:', error);
      return true;
    } finally {
      setState(prev => ({ ...prev, isCheckingLimit: false }));
    }
  };

  // === INICIAR LIVE ===
  const startLive = async () => {
    console.log("🔥 CLICK: startLive button clicked!");
    clearError();
    const canProceed = await checkLiveLimit();
    console.log("Proceed check:", canProceed);
    if (!canProceed) return;
    setState(prev => ({ ...prev, isLive: true, showVideo: true }));
  };

  // === CÂMERA ===
  const initializeCamera = async () => {
    console.log("Initializing camera in LiveSession...");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: true
      });
      console.log("Successfully got user media stream in LiveSession");
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // Wait for video metadata to be loaded before playing
        await new Promise((resolve) => {
          videoRef.current.onloadedmetadata = () => resolve();
        });
        await videoRef.current.play();
        console.log("Video playing successfully in LiveSession");
        setState(prev => ({ ...prev, cameraReady: true }));
      } else {
        console.warn("videoRef.current is null in LiveSession");
        // Fallback if ref is not yet assigned during rendering
        setTimeout(async () => {
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            await videoRef.current.play();
            setState(prev => ({ ...prev, cameraReady: true }));
          }
        }, 100);
      }
    } catch (err) {
      console.error("Camera initialization failed in LiveSession:", err);
      setError('Câmera: ' + err.message + '. Por favor, verifique as permissões.');
      setState(prev => ({ ...prev, cameraReady: false }));
    }
  };

  // === GRAVAR CLIP ===
  const startClipRecording = useCallback(() => {
    if (!streamRef.current) return;
    console.log('INICIANDO GRAVAÇÃO DO CLIP...');
    setState(prev => ({ ...prev, isCounting: true }));
    let count = 3;

    const countdown = setInterval(() => {
      setState(prev => ({ ...prev, countdown: count }));
      count--;
      if (count < 0) {
        clearInterval(countdown);
        setState(prev => ({
          ...prev,
          isCounting: false,
          isRecording: true,
          recordingTime: CLIP_DURATION
        }));

        recordedChunksRef.current = [];
        try {
          const options = { mimeType: 'video/webm;codecs=vp9,opus', videoBitsPerSecond: 2500000 };
          const recorder = new MediaRecorder(streamRef.current, options);
          mediaRecorderRef.current = recorder;

          recorder.ondataavailable = (e) => {
            if (e.data && e.data.size > 0) {
              recordedChunksRef.current.push(e.data);
            }
          };

          recorder.onstop = () => {
            console.log("GRAVAÇÃO CONCLUÍDA - Total chunks:", recordedChunksRef.current.length);
            setState(prev => ({ ...prev, isRecording: false }));
          };

          recorder.start(1000);
        } catch (err) {
          console.error('Erro ao iniciar MediaRecorder:', err);
          try {
            const fallback = new MediaRecorder(streamRef.current);
            mediaRecorderRef.current = fallback;
            fallback.start();
          } catch (fallbackErr) {
            console.error('Fallback falhou:', fallbackErr);
            setError('Erro ao gravar clip');
          }
        }

        setTimeout(() => {
          if (mediaRecorderRef.current?.state === 'recording') {
            mediaRecorderRef.current.stop();
          }
        }, CLIP_DURATION * 1000);
      }
    }, 1000);
  }, []);

  // === TIMERS ===
  useEffect(() => {
    if (!state.isLive || state.timeLeft <= 0) return;
    liveTimerRef.current = setInterval(() => {
      setState(prev => {
        const newTime = prev.timeLeft - 1;
        if (newTime === CLIP_START_AT && !prev.isRecording) startClipRecording();
        if (newTime <= 0) {
          clearInterval(liveTimerRef.current);
          completeLive();
          return { ...prev, timeLeft: 0 };
        }
        return { ...prev, timeLeft: newTime };
      });
    }, 1000);
    return () => clearInterval(liveTimerRef.current);
  }, [state.isLive, state.timeLeft, startClipRecording]);

  useEffect(() => {
    if (state.showVideo && !state.cameraReady) initializeCamera();
  }, [state.showVideo, state.cameraReady]);

  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach(t => t.stop());
      mediaRecorderRef.current?.stop();
      clearInterval(liveTimerRef.current);
    };
  }, []);

  // === FINALIZAR LIVE (COM txHash NO CARD) ===
  const completeLive = async () => {
    console.log("FINALIZANDO LIVE...");
    try {
      // === VERIFICAR AUTENTICAÇÃO (mas NÃO bloquear fluxo) ===
      const token = localStorage.getItem('token');
      const hasToken = token && token !== 'undefined' && token !== 'null' && token.trim().length > 10;
      if (!hasToken) {
        console.warn("Token não encontrado/inválido — seguindo com fallback local. Faça login para vincular no backend.");
        setState(prev => ({ ...prev, error: 'Sessão local: faça login para vincular no backend.' }));
      } else {
        console.log("🔐 Token detectado (não validado aqui) — seguiremos tentando usar o backend");
      }

      // stop recording and camera
      mediaRecorderRef.current?.stop();
      streamRef.current?.getTracks().forEach(t => t.stop());

      let finalClipBase64 = null;
      if (recordedChunksRef.current.length > 0) {
        const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
        finalClipBase64 = await blobToBase64(blob);
        finalClipBase64 = normalizeBase64(finalClipBase64);
      }
      if (!finalClipBase64) throw new Error("Clip não capturado");

      // prepare headers only if we have token
      const headers = hasToken ? {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token.trim()}`
      } : { 'Content-Type': 'application/json' };

      // === PASSO 1: UPLOAD IPFS (tentar se temos token) ===
      console.log("PASSO 1: Fazendo upload para IPFS (se possível)...");
      const payload = {
        duration: LIVE_DURATION,
        frame: finalClipBase64,
        timestamp: new Date().toISOString(),
        frameLength: finalClipBase64.length,
        txHash: null
      };

      let result = { frameUrl: null, tokensEarned: null, totalTokens: null, uploadMethod: null, message: null, txHash: null };
      if (hasToken) {
        try {
          const response = await fetch(`${API_URL}/api/complete-live`, {
            method: 'POST',
            headers,
            body: JSON.stringify(payload),
          });
          if (!response.ok) {
            const errorText = await response.text();
            console.warn("Upload returned non-ok:", response.status, errorText);
            if (response.status === 401 || response.status === 403) {
              // token invalid/expired: inform user but continue with local fallback
              setState(prev => ({ ...prev, error: 'Sessão expirada. Gerando prova localmente; faça login para vincular no backend.' }));
              result = { frameUrl: null, tokensEarned: null, totalTokens: null, uploadMethod: null, message: null, txHash: null };
            } else {
              throw new Error(`HTTP ${response.status}: ${errorText}`);
            }
          } else {
            result = await response.json();
          }
        } catch (uploadErr) {
          console.error("Erro no upload IPFS:", uploadErr);
          // fallback: continue without backend result
          result = { frameUrl: null, tokensEarned: null, totalTokens: null, uploadMethod: null, message: null, txHash: null };
        }
      } else {
        console.log("Pulando upload para backend — sem token. Usando fallback local.");
      }

      // === PASSO 2: EXTRAIR CID DO IPFS (se disponível) ===
      let videoCID = '', audioCID = '';
      if (result && result.frameUrl && isIPFSURL(result.frameUrl)) {
        videoCID = extractIPFSCID(result.frameUrl) || '';
        console.log("CID extraído do IPFS:", videoCID);
      } else {
        console.warn("CID não disponível a partir do upload backend");
      }

      // === PASSO 3: GERAR HASH COM CID E ENVIAR PROOF PARA BLOCKCHAIN (sempre tentar se wallet conectada) ===
      let proofResult = null;
      if (state.walletConnected && state.isPolygon) {
        try {
          setState(prev => ({ ...prev, isGeneratingProof: true }));
          console.log("PASSO 3: Gerando hash com CID e enviando proof para blockchain...");
          const frameBlob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
          const frameHash = await sha256(frameBlob);
          const audioHash = frameHash;

          const liveData = {
            videoFrameHash: frameHash,
            audioSampleHash: audioHash,
            timestamp: Date.now(),
            latitude: 0,
            longitude: 0,
            deviceFingerprint: navigator.userAgent,
            videoCID: videoCID || '',
            audioCID: audioCID || ''
          };

          proofResult = await submitProofOfLife(liveData);
          console.log("PROOF ENVIADA COM CID:", proofResult?.txHash || '(sem txHash)');
          setState(prev => ({ ...prev, isGeneratingProof: false }));

          // === PASSO 4: ATUALIZAR BACKEND COM txHash (se temos token e txHash) ===
          if (proofResult?.txHash && hasToken) {
            try {
              const updateHeaders = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token.trim()}`
              };
              const updateResponse = await fetch(`${API_URL}/api/update-live-txhash`, {
                method: 'POST',
                headers: updateHeaders,
                body: JSON.stringify({
                  timestamp: payload.timestamp,
                  txHash: proofResult.txHash
                })
              });
              if (updateResponse.ok) {
                console.log("Backend atualizado com txHash");
                result.txHash = proofResult.txHash;
              } else {
                const errorText = await updateResponse.text();
                console.warn("Falha ao atualizar backend:", updateResponse.status, errorText);
              }
            } catch (updateErr) {
              console.warn("Falha ao atualizar backend com txHash (não crítico):", updateErr);
            }
          }
        } catch (proofErr) {
          console.error("FALHA NO PROOF:", proofErr);
          proofResult = { error: proofErr.message };
          setState(prev => ({ ...prev, isGeneratingProof: false }));
        }
      } else {
        console.log("Carteira não conectada ou não em Polygon — pulando envio de proof on-chain.");
      }

      // === MONTAR DADOS E CHAMAR onComplete ===
      const finalTxHash = proofResult?.txHash || result.txHash || null;
      const hasProof = !!finalTxHash;
      const userMessage = hasProof
        ? `+10 RUA mintado! Tx: ${finalTxHash.slice(0, 10)}...`
        : state.walletConnected
          ? "Live salva! Conecte na Polygon para ganhar RUA"
          : "Live salva com sucesso!";

      console.log("✅ Live concluída - preparando dados finais", { finalTxHash, frameUrl: result.frameUrl, tokensEarned: result.tokensEarned });

      safeOnComplete({
        success: true,
        tokensEarned: result.tokensEarned || (hasProof ? 10 : 0),
        totalTokens: result.totalTokens || null,
        message: result.message || 'Live concluída!',
        feedback: result.feedback || null,
        frameUrl: result.frameUrl || null,
        uploadMethod: result.uploadMethod || null,
        userMessage,
        proofResult: proofResult || null,
        txHash: finalTxHash,
        polygonScanUrl: finalTxHash ? `https://polygonscan.com/tx/${finalTxHash}` : null,
        alreadyUploaded: !!result.frameUrl,
        processedByLiveSession: true,
        walletConnected: state.walletConnected,
        isPolygon: state.isPolygon,
        userAddress: state.userAddress,
        showRewards: true,
        liveCompleted: true,
        videoCID: videoCID || null
      });

      // Limpar estado
      setState(prev => ({
        ...prev,
        isLive: false,
        showVideo: false,
        timeLeft: LIVE_DURATION,
        capturedClip: null,
        isGeneratingProof: false,
        showSuccessCard: true,
        finalResult: {
          txHash: finalTxHash,
          tokensEarned: result.tokensEarned || (hasProof ? 10 : 0),
          polygonScanUrl: finalTxHash ? `https://polygonscan.com/tx/${finalTxHash}` : null,
          videoCID: videoCID || null,
          clipUrl: result.frameUrl || null
        }
      }));
    } catch (err) {
      console.error('ERRO FINAL:', err);
      setState(prev => ({ ...prev, isGeneratingProof: false }));
      safeOnComplete({ error: err.message || String(err), success: false });
    }
  };

  // === CANCELAR ===
  const cancelLive = () => {
    mediaRecorderRef.current?.stop();
    streamRef.current?.getTracks().forEach(t => t.stop());
    setState({
      isLive: false, showVideo: false, cameraReady: false, timeLeft: LIVE_DURATION,
      isCounting: false, isRecording: false, recordingTime: CLIP_DURATION,
      capturedClip: null,
      walletConnected: state.walletConnected,
      userAddress: state.userAddress,
      ruaBalance: state.ruaBalance,
      isPolygon: state.isPolygon,
    });
  };

  // === RENDER ===
  const { error, showVideo, walletConnected, userAddress, ruaBalance, isPolygon, isCheckingLimit, isGeneratingProof } = state;
  return (
    <div className="relative max-w-2xl mx-auto p-6">
      {isGeneratingProof && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-32 h-32 mx-auto mb-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center animate-pulse">
              <div className="text-white text-2xl">⚡</div>
            </div>
            <p className="text-white text-xl font-bold">Loading AI Models...</p>
            <p className="text-purple-300 text-sm mt-2">Native PlexoNatural Heuristic Engine</p>
            <p className="text-purple-400 text-xs mt-1 italic">Building specialized customer profile</p>
          </div>
        </div>
      )}

      <audio
        ref={proofReadyAudioRef}
        preload="auto"
        style={{ display: 'none' }}
      />
      <div className="fixed bottom-4 left-4 bg-black bg-opacity-70 text-white text-xs px-3 py-1 rounded-full flex items-center space-x-1">
        <span>agradecemos ao</span>
        <span className="font-bold text-green-400">DeepSeek</span>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          <p className="font-bold">Erro:</p>
          <p>{error}</p>
        </div>
      )}

      {!showVideo ? (
        <div className="space-y-4">
          {!walletConnected && (
            <div className="bg-gradient-to-r from-violet-600 to-green-500 p-4 rounded-lg text-white">
              <p className="font-bold flex items-center">
                <Wallet className="w-5 h-5 mr-2" />
                Conecte para ganhar RUA
              </p>
              <button onClick={handleConnectWallet} className="mt-2 w-full bg-white text-violet-600 font-bold py-2 rounded-lg transition hover:scale-105">
                Conectar Carteira
              </button>
            </div>
          )}

          {walletConnected && (
            <div className={`p-3 rounded-lg text-sm ${isPolygon ? 'bg-green-100' : 'bg-yellow-100'}`}>
              <p>Carteira: {userAddress?.slice(0, 6)}...{userAddress?.slice(-4)}</p>
              <p>Saldo RUA: {ruaBalance ?? '—'} RUA</p>
              {!isPolygon && (
                <p className="text-orange-600 mt-1">
                  Conecte na Polygon para ganhar RUA
                </p>
              )}
            </div>
          )}

          <button
            onClick={startLive}
            disabled={isCheckingLimit}
            className={`w-full py-4 rounded-lg text-xl font-bold flex items-center justify-center transition ${isCheckingLimit ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-violet-600 to-green-500 text-white hover:from-violet-700 hover:to-green-600'
              }`}
          >
            <Camera className="w-6 h-6 mr-2" />
            Go Live (1 min + Clip 5s)
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="relative bg-black rounded-lg overflow-hidden">
            <video ref={videoRef} autoPlay playsInline muted className="w-full h-96 object-cover" />
            {state.isLive && (
              <div className="absolute top-4 right-4 bg-purple-600 text-white px-4 py-2 rounded-full font-bold flex items-center">
                <Clock className="w-5 h-5 mr-2" />
                {formatTime(state.timeLeft)}
              </div>
            )}
            {state.isCounting && (
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-70">
                <div className="text-8xl font-bold text-white">{state.countdown}</div>
              </div>
            )}
            {state.isRecording && (
              <div className="absolute top-4 left-4 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold animate-pulse">
                GRAVANDO CLIP
              </div>
            )}
          </div>
          <button onClick={cancelLive} className="w-full bg-red-600 text-white py-3 rounded-lg font-bold transition hover:bg-red-700">
            Finalizar Live
          </button>
        </div>
      )}

      {state.showSuccessCard && state.finalResult && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl text-black">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Live Concluída!</h3>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="text-3xl font-bold text-green-600">+{state.finalResult.tokensEarned} tokens</div>
              </div>

              {state.finalResult.txHash && (
                <div className="bg-gradient-to-r from-violet-50 to-green-50 border-2 border-violet-200 rounded-lg p-4 mb-4 text-left">
                  <p className="font-bold text-violet-800 mb-1">Prova Eterna na Polygon:</p>
                  <a
                    href={state.finalResult.polygonScanUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-violet-600 hover:text-violet-800 font-mono text-xs break-all underline flex items-center"
                  >
                    <Globe className="w-4 h-4 mr-1 flex-shrink-0" />
                    {state.finalResult.txHash}
                  </a>
                  <p className="text-[10px] text-gray-500 mt-2 italic">with Grok services · Deus no centro</p>
                </div>
              )}

              <div className="my-4 flex justify-center">
                <AdPlaceholder position="banner" />
              </div>

              <button
                onClick={() => setState(prev => ({ ...prev, showSuccessCard: false }))}
                className="w-full mt-4 bg-gray-800 text-white py-3 rounded-lg font-bold"
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Simple icon fallbacks since we might not have them imported
const CheckCircle = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const Globe = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
  </svg>
);

export default LiveSession;